import { queryOptions } from "@tanstack/react-query";
import type { McpAccess, McpClient, OAuthIdpService } from "@/services/oauth-idp.service";
import { FRESHNESS } from "./policies";
import { GLOBAL_TENANT, scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The OAuth app and MCP access reads (NG-2814):
 *
 * - `oauthApps.list`: a workspace's registered OAuth apps,
 *   `[slug, uid, 'oauth-apps', 'list']`, or the instance-wide ones an instance
 *   admin registers, `['*', uid, 'oauth-apps', 'list']`.
 * - `oauthApp.detail`: one app, `[slug | '*', uid, 'oauth-app', id]`.
 * - `oauthConsents.mine`: the signed-in user's own OAuth grants,
 *   `['*', uid, 'oauth-consents', 'mine']`.
 * - `mcp.access`: a workspace's MCP access settings,
 *   `[slug, uid, 'mcp', 'access']`.
 * - `mcp.clients`: who is connected over MCP, `[slug, uid, 'mcp', 'clients']`.
 *   Gated on `workspace.member.manage`: a 403 is the answer for anyone else,
 *   so it is expected and logged as a warning.
 *
 * All are admin or settings screens that edit what they read, so all are
 * mutable; all are plain GETs, so all declare `"safe-read"`. The instance list
 * also answers 403 to a non-admin, which its page shows as an empty state.
 *
 * @writers The screens write through `OAuthIdpService` and then, via
 * `app/hooks/use-oauth-apps.ts`: create and delete re-read the app list; an
 * app's save, redirect URI add and remove replace or patch its detail entry
 * (and the save its list row); revoking a consent drops it from the user's
 * list; an MCP save replaces the access entry, disable re-reads it and the
 * clients, and revoking a client drops it from the clients list.
 */

/** Where an app lives: a workspace's apps, or the instance-wide ones. */
export type OAuthAppOwner = { kind: "workspace"; workspaceSlug: string } | { kind: "instance" };

/** The tenant an owner's apps are cached under. */
export function oauthAppTenant(owner: OAuthAppOwner): string {
  return owner.kind === "workspace" ? owner.workspaceSlug : GLOBAL_TENANT;
}

export function oauthAppsKey(scope: QueryScope) {
  return scopedKey(scope, "oauth-apps", "list");
}

export function oauthAppKey(scope: QueryScope, appId: string) {
  return scopedKey(scope, "oauth-app", appId);
}

export function myOAuthConsentsKey(scope: QueryScope) {
  return scopedKey(scope, "oauth-consents", "mine");
}

export function mcpAccessKey(scope: QueryScope) {
  return scopedKey(scope, "mcp", "access");
}

export function mcpClientsKey(scope: QueryScope) {
  return scopedKey(scope, "mcp", "clients");
}

type AppReads = Pick<
  OAuthIdpService,
  "listWorkspaceApps" | "listInstanceApps" | "getWorkspaceApp" | "getInstanceApp"
>;

/** An owner's OAuth apps; `scope` is the owner's tenant. */
export function oauthAppsQuery(service: AppReads, scope: QueryScope, owner: OAuthAppOwner) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: oauthAppsKey(scope),
    queryFn: scopedRead(
      (request) =>
        owner.kind === "workspace"
          ? service.listWorkspaceApps(owner.workspaceSlug, request)
          : service.listInstanceApps(request),
      { retry: "safe-read" },
    ),
    meta:
      owner.kind === "instance"
        ? { ...FRESHNESS.mutable.meta, expectedStatuses: [403] }
        : FRESHNESS.mutable.meta,
  });
}

/** One OAuth app of an owner. */
export function oauthAppQuery(
  service: AppReads,
  scope: QueryScope,
  owner: OAuthAppOwner,
  appId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: oauthAppKey(scope, appId),
    queryFn: scopedRead(
      (request) =>
        owner.kind === "workspace"
          ? service.getWorkspaceApp(owner.workspaceSlug, appId, request)
          : service.getInstanceApp(appId, request),
      { retry: "safe-read" },
    ),
  });
}

/** The signed-in user's own OAuth grants; `scope` is under `GLOBAL_TENANT`. */
export function myOAuthConsentsQuery(
  service: Pick<OAuthIdpService, "listMyConsents">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myOAuthConsentsKey(scope),
    queryFn: scopedRead((request) => service.listMyConsents(request), { retry: "safe-read" }),
  });
}

/** A workspace's MCP access settings. */
export function mcpAccessQuery(service: Pick<OAuthIdpService, "getMcpAccess">, scope: QueryScope) {
  return queryOptions<McpAccess>({
    ...FRESHNESS.mutable,
    queryKey: mcpAccessKey(scope),
    queryFn: scopedRead((request) => service.getMcpAccess(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** Who is connected to a workspace over MCP; a 403 means the viewer may not see it. */
export function mcpClientsQuery(
  service: Pick<OAuthIdpService, "listMcpClients">,
  scope: QueryScope,
) {
  return queryOptions<McpClient[]>({
    ...FRESHNESS.mutable,
    queryKey: mcpClientsKey(scope),
    queryFn: scopedRead((request) => service.listMcpClients(scope.tenant, request), {
      retry: "safe-read",
    }),
    meta: { ...FRESHNESS.mutable.meta, expectedStatuses: [403] },
  });
}
